import { useEffect, useMemo, useRef, useState } from 'react'
import { Form } from 'react-final-form'
import { useTranslation } from 'react-i18next'

import _ from 'lodash'

import { useViewportSize } from '../../hooks/useViewportSize'
import { SelectField } from '../../containers/FormFields/SelectField'
import { createFormValidator, required, email, maxLengthInterceptor, phoneNumber } from '../../utils/validation'
import { ThemeWrapper } from '../theme'
import { Text } from '../../components/Text'
import { Flex } from '../Flex'
import { Space } from '../Space'
import { SelectOption } from '../Select'
import { ContactUsDetails } from '../layout/HeaderManager'
import { InputField } from '../../containers/FormFields/InputField'
import { TextAreaField } from '../../containers/FormFields/TextAreaField'
import { Dictionary } from '../../utils/types'

import {
  ContactFormStickyWrapper,
  StickyWrapper,
  StyledImage,
  SubmitButton,
  Title,
  TitleWrapper,
  UrlWrapper,
  ErrorMessage,
  SuccessMessage
} from './styled'

type FormData = ContactUsDetails

export type ContactUsFormProps = {
  handleContactUsRequestSubmit?: (data: ContactUsDetails) => void
  contactRequestLoading?: boolean
  contactRequestError?: any
  contactRequestDone?: boolean
  contactUsReason?: Dictionary<string>
  resetContactUs?: () => void
}

export const ContactFormWidget = ({
  handleContactUsRequestSubmit,
  contactRequestLoading: loading,
  contactRequestError: error,
  contactRequestDone: done,
  contactUsReason,
  resetContactUs
}: ContactUsFormProps) => {
  const { t } = useTranslation(['account', 'core'])
  const ta = (key: string) => t(`account:ContactUs.${key}`)
  const [isExpanded, setIsExpanded] = useState(false)
  const showFullWidget = () => {
    resetContactUs && resetContactUs()
    setIsExpanded(!isExpanded)
  }

  const validator = useMemo(
    () =>
      createFormValidator<FormData>({
        name: [required()],
        email: [required(), email()],
        phone: [required(), phoneNumber()],
        reason: [required()],
        message: [required()]
      }),
    []
  )

  const handleSubmit = async (values: FormData) => {
    const errs = validator()(values)
    if (errs) {
      return errs
    }
    handleContactUsRequestSubmit && handleContactUsRequestSubmit(values)
  }

  const reasonList: SelectOption[] = _.sortBy(
    _.map(_.keys(contactUsReason), key => ({
      value: key,
      label: contactUsReason ? contactUsReason[key] : ''
    })),
    x => x.value
  )

  const sortedReasonList = _.concat(
    _.filter(reasonList, o => o.value != 'Others'),
    _.filter(reasonList, o => o.value == 'Others')
  )

  const contactUsExpandedComponentRef = useRef<HTMLDivElement>(null)
  const [bottomAndOverflow, setBottomAndOverflow] = useState<boolean>(true)
  const currentFooterRef = contactUsExpandedComponentRef?.current
  const viewPort = useViewportSize()
  const top =
    viewPort?.viewportHeight && !viewPort?.isMobile
      ? `${_.floor(viewPort?.viewportHeight / (viewPort?.isWide ? 2.5 : 2))}px`
      : '280px'

  useEffect(() => {
    if (isExpanded) {
      const top = currentFooterRef?.getBoundingClientRect().top
      const height = currentFooterRef?.scrollHeight
      if (top && height && viewPort?.viewportHeight && top + height > viewPort?.viewportHeight) {
        setBottomAndOverflow(true)
      } else {
        setBottomAndOverflow(false)
      }
    }
  }, [currentFooterRef, viewPort, isExpanded])

  const timeoutId = useRef<NodeJS.Timeout | undefined>()

  useEffect(() => {
    if (done && !error && timeoutId) {
      timeoutId.current && clearTimeout(timeoutId.current)
      timeoutId.current = setTimeout(() => {
        setIsExpanded(false)
      }, 5000)
    }
    return () => {
      timeoutId && timeoutId.current && clearTimeout(timeoutId.current)
    }
  }, [done, error, timeoutId])

  return (
    <ThemeWrapper>
      <Flex>
        <StickyWrapper top={top}>
          <TitleWrapper alignItems="center" onClick={showFullWidget}>
            <StyledImage alt="Mail icon" type="mailWhite" />
            <Text>{ta('Title')}</Text>
          </TitleWrapper>
        </StickyWrapper>
        {isExpanded && (
          <ContactFormStickyWrapper ref={contactUsExpandedComponentRef} bottomAndOverflow={bottomAndOverflow} top={top}>
            <Title>{ta('Title')}</Title>
            {done && !error && <SuccessMessage>{ta('SuccessfullySubmitted')}</SuccessMessage>}
            {(!done || (done && error)) && (
              <Form
                initialValues={{}}
                validate={validator}
                onSubmit={handleSubmit}
                render={({ handleSubmit }) => (
                  <form onSubmit={handleSubmit}>
                    <Space direction="vertical" space={1} flexDirection="column">
                      <InputField
                        name="name"
                        label={`${ta('Name')}*`}
                        additionalProps={{ disabled: loading, size: 'small' }}
                      />
                      <InputField
                        name="email"
                        label={`${ta('Email')}*`}
                        additionalProps={{ disabled: loading, size: 'small' }}
                      />
                      <UrlWrapper>
                        <InputField name="url" label="url" />
                      </UrlWrapper>
                      <InputField
                        name="phone"
                        label={`${ta('Contact')}*`}
                        additionalProps={{ disabled: loading, size: 'small' }}
                      />
                      <SelectField
                        label={`${ta('Reason')}*`}
                        name="reason"
                        hideOnSingle={false}
                        options={sortedReasonList}
                        additionalProps={{ disabled: loading, size: 'small' }}
                      />
                      <TextAreaField
                        label={`${ta('Message')}*`}
                        name="message"
                        labelProps={{ fontWeight: 500 }}
                        additionalProps={{
                          rows: 3,
                          disabled: loading
                        }}
                        interceptor={maxLengthInterceptor(250)}
                      />
                      {done && error && <ErrorMessage>{ta('ErrorDuringSubmission')}</ErrorMessage>}
                      <SubmitButton htmlType="submit" variant="primary" size="large" disabled={loading}>
                        {`${ta('Submit')}`}
                      </SubmitButton>
                    </Space>
                  </form>
                )}
              />
            )}
          </ContactFormStickyWrapper>
        )}
      </Flex>
    </ThemeWrapper>
  )
}
